import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BanknoteIcon, CreditCardIcon, GiftIcon, LineChartIcon, PiggyBankIcon, PlusIcon, Trash2Icon, WalletIcon, LandmarkIcon, ShieldCheckIcon } from 'lucide-react';
import { StepFrame, FieldGrid, Callout } from '../StepFrame';
import { ChoiceCard } from '../../ui/ChoiceCard';
import { HelpPanel } from '../../ui/HelpPanel';
import { TextField } from '../../ui/TextField';
import { Button } from '../../ui/Button';
import { Sheet } from '../../ui/Sheet';
import { YesNo } from '../../ui/YesNo';
import { AnimatedNumber } from '../../ui/AnimatedNumber';
import { usePortal } from '../../../contexts/PortalContext';
import { formatAmountInput, formatCurrency, parseAmount, uid } from '../../../utils/format';
import type { AssetType, OwnedProperty } from '../../../types/application';
const ease = [0.23, 1, 0.32, 1] as const;
const assetTypes: {
  v: AssetType;
  t: string;
  i: React.ReactNode;
}[] = [{
  v: 'checking',
  t: 'Checking',
  i: <WalletIcon className="h-5 w-5" data-id="element-143" data-mp="3hqrwzho8rs0q" />
}, {
  v: 'savings',
  t: 'Savings',
  i: <PiggyBankIcon className="h-5 w-5" data-id="element-144" data-mp="3mq4km4mxt34d" />
}, {
  v: 'retirement',
  t: 'Retirement',
  i: <LandmarkIcon className="h-5 w-5" data-id="element-145" data-mp="3mnqodsgitkv8" />
}, {
  v: 'brokerage',
  t: 'Brokerage',
  i: <LineChartIcon className="h-5 w-5" data-id="element-146" data-mp="38ptsyzdszki5" />
}, {
  v: 'gift',
  t: 'Gift funds',
  i: <GiftIcon className="h-5 w-5" data-id="element-147" data-mp="3dode4xkkos9g" />
}, {
  v: 'other',
  t: 'Other',
  i: <BanknoteIcon className="h-5 w-5" data-id="element-148" data-mp="2z0sbt5viux2l" />
}];
export function AccountsStep() {
  const {
    answers,
    update
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<AssetType>('savings');
  const [institution, setInstitution] = useState('');
  const [balance, setBalance] = useState('');
  const total = answers.assets.reduce((s, a) => s + parseAmount(a.balance), 0);
  const needed = answers.downPayment + Math.round(answers.price * 0.025);
  const pct = Math.min(100, total / needed * 100);
  const add = () => {
    update({
      assets: [...answers.assets, {
        id: uid('a'),
        type,
        institution: institution || (type === 'gift' ? 'Gift' : 'Account'),
        balance
      }]
    });
    setOpen(false);
    setInstitution('');
    setBalance('');
  };
  return <StepFrame title="Where’s the money for your down payment?" description="Add the accounts you’ll use for your down payment and closing costs. Rough balances are fine." help={<>
          <HelpPanel question="What counts as an asset?" data-id="element-150" data-mp="19jep5x9p38v1">
            Checking, savings, CDs, money market, retirement (401k, IRA), brokerage accounts, and gift money from family. Cars and personal belongings don’t count.
          </HelpPanel>
          <HelpPanel question="Can family help with my down payment?" data-id="element-151" data-mp="10hf3grnqjglr">
            Yes. Add it as gift funds. We’ll later ask the giver to sign a short letter confirming it’s a gift, not a loan.
          </HelpPanel>
        </>} data-id="element-149" data-mp="lrzdvezxxh7n">
      <div className="rounded-2xl bg-ink-900 p-5 text-white" data-id="element-152" data-mp="3l6kzllqys9zd">
        <div className="flex items-end justify-between" data-id="element-153" data-mp="3g9316pakpd5m">
          <div data-id="element-154" data-mp="36662doqkqb4n">
            <p className="text-sm text-white/60" data-id="element-155" data-mp="366p9sbillh4f">Total assets</p>
            <AnimatedNumber value={total} className="mt-1 block text-3xl font-semibold tracking-tight" data-id="element-156" data-mp="3b50i2ht8vjkj" />
          </div>
          <p className="tabular text-right text-sm text-white/60" data-id="element-157" data-mp="3g3k5ynref5fj">
            Est. needed
            <span className="block text-white" data-id="element-158" data-mp="3kuk39u4fjpik">{formatCurrency(needed)}</span>
          </p>
        </div>
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10" data-id="element-159" data-mp="3owno39czo6gi">
          <motion.div className={`h-full rounded-full ${pct >= 100 ? 'bg-[#7EE2B5]' : 'bg-pulse-300'}`} animate={{
          width: `${pct}%`
        }} transition={{
          duration: 0.5,
          ease
        }} data-id="element-160" data-mp="3oyrvw2e0dpm2" />
        </div>
        <p className="mt-3 text-[13px] text-white/60" data-id="element-161" data-mp="2n4bjixrgeyr">{pct >= 100 ? 'You’ve covered your down payment and estimated closing costs.' : `Down payment plus about ${formatCurrency(needed - answers.downPayment)} in closing costs.`}</p>
      </div>

      <ul className="mt-4 divide-y divide-ink-100 overflow-hidden rounded-2xl bg-white ring-1 ring-ink-200" data-id="element-162" data-mp="29evecx7m8gf4">
        <AnimatePresence initial={false} data-id="element-163" data-mp="3qbisyeh2rbc">
          {answers.assets.map(a => {
          const t = assetTypes.find(x => x.v === a.type);
          return <motion.li key={a.id} layout initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.22,
            ease
          }} data-id="element-164" data-mp="crswka4eqgwv">
                <div className="flex items-center gap-3 p-4" data-id="element-165" data-mp="3g8rlaxismtho">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-ink-100 text-ink-700" data-id="element-166" data-mp="3g9b5ylztvvze">{t?.i}</span>
                  <div className="min-w-0 flex-1" data-id="element-167" data-mp="3c53ewi5we3y0">
                    <p className="truncate font-medium text-ink-900" data-id="element-168" data-mp="3pzmj8z5ejhiy">{a.institution}</p>
                    <p className="text-sm text-ink-500" data-id="element-169" data-mp="3l7md2oyqnsdx">{t?.t}</p>
                  </div>
                  <span className="tabular font-medium text-ink-900" data-id="element-170" data-mp="2xhs1eaq5o9v9">{formatCurrency(parseAmount(a.balance))}</span>
                  <button onClick={() => update({
                assets: answers.assets.filter(x => x.id !== a.id)
              })} className="grid h-9 w-9 place-items-center rounded-lg text-ink-400 transition-colors duration-150 hover:bg-ink-100 hover:text-critical-500" aria-label={`Remove ${a.institution}`} data-id="element-171" data-mp="2sjyvlisrlhvi">
                    <Trash2Icon className="h-4 w-4" data-id="element-172" data-mp="z0zyevhvlrgx" />
                  </button>
                </div>
              </motion.li>;
        })}
        </AnimatePresence>
        <li data-id="element-173" data-mp="1rp5kblko1mic">
          <button onClick={() => setOpen(true)} className="flex w-full items-center gap-3 p-4 text-left font-medium text-pulse-700 transition-colors duration-150 hover:bg-pulse-50" data-id="element-174" data-mp="1rpf24lvk4ncy">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-pulse-50 text-pulse-600" data-id="element-175" data-mp="1hqy6vcpl5ml3">
              <PlusIcon className="h-5 w-5" data-id="element-176" data-mp="1mq2dpwp1f889" />
            </span>
            Add an account
          </button>
        </li>
      </ul>

      <Sheet open={open} onClose={() => setOpen(false)} title="Add an account" description="You can connect statements later — just a balance for now." data-id="element-177" data-mp="6bj7615zsgma">
        <div role="radiogroup" aria-label="Account type" className="grid grid-cols-3 gap-2" data-id="element-178" data-mp="b8j67s4fcj3w">
          {assetTypes.map(t => <button key={t.v} role="radio" aria-checked={type === t.v} onClick={() => setType(t.v)} className={`flex flex-col items-center gap-2 rounded-xl p-3 text-sm font-medium ring-1 ring-inset transition-[background-color,box-shadow,color] duration-150 ${type === t.v ? 'bg-pulse-50 text-pulse-700 ring-2 ring-pulse-600' : 'text-ink-700 ring-ink-200 hover:ring-ink-300'}`} data-id="element-179" data-mp="3jc1zmpjcqyxe">
              {t.i}
              {t.t}
            </button>)}
        </div>
        <div className="mt-5 space-y-4" data-id="element-180" data-mp="2vn98r7hl7h9n">
          <TextField label={type === 'gift' ? 'Who is the gift from?' : 'Bank or institution'} placeholder={type === 'gift' ? 'e.g. Parents' : 'e.g. Ally, Fidelity'} value={institution} onChange={setInstitution} data-id="element-181" data-mp="2qo66fuszrp5d" />
          <TextField label={type === 'gift' ? 'Gift amount' : 'Current balance'} prefix="$" inputMode="numeric" value={formatAmountInput(balance)} onChange={v => setBalance(v.replace(/[^0-9]/g, ''))} data-id="element-182" data-mp="ow9s6dffw16w" />
        </div>
        <Button block size="lg" className="mt-6" disabled={!balance} onClick={add} data-id="element-183" data-mp="11dudcmxswkgt">
          Add account
        </Button>
      </Sheet>
    </StepFrame>;
}
export function RealEstateStep() {
  const {
    answers,
    update
  } = usePortal();
  const list = answers.properties;
  const setProp = (id: string, patch: Partial<OwnedProperty>) => update({
    properties: list.map(p => p.id === id ? {
      ...p,
      ...patch
    } : p)
  });
  return <StepFrame title="Do you own any other real estate?" help={<HelpPanel question="Why are you asking about other properties?" data-id="element-185" data-mp="ndt0e949hmkz">
          Payments on homes you own affect how much you can afford. If you’re selling one, its proceeds may also help with your down payment.
        </HelpPanel>} data-id="element-184" data-mp="iido3llwjyqo">
      <YesNo label="Own other real estate" value={answers.ownsOtherProperty} onChange={v => update({
      ownsOtherProperty: v,
      properties: v === 'yes' && list.length === 0 ? [{
        id: uid('p'),
        address: '',
        value: '',
        plan: 'keep',
        hasMortgage: true,
        monthlyPayment: ''
      }] : list
    })} data-id="element-186" data-mp="22vusbmofufhi" />
      <AnimatePresence initial={false} data-id="element-187" data-mp="2ynzrzym9d1cx">
        {answers.ownsOtherProperty === 'yes' && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="overflow-hidden" data-id="element-188" data-mp="2tx186p229l4x">
            <div className="space-y-4 pt-6" data-id="element-189" data-mp="32pbhm454tml4">
              {list.map(p => <section key={p.id} className="rounded-2xl bg-white p-4 ring-1 ring-ink-200 md:p-5" aria-label="Owned property" data-id="element-190" data-mp="32rfomuybv1pm">
                  <TextField label="Property address" value={p.address} onChange={v => setProp(p.id, {
              address: v
            })} data-id="element-191" data-mp="3cmzfv41y5c81" />
                  <div className="mt-4" data-id="element-192" data-mp="1n941nfe91bgj">
                    <FieldGrid data-id="element-193" data-mp="1na5o0g0ptkdk">
                      <TextField label="Estimated value" prefix="$" inputMode="numeric" value={formatAmountInput(p.value)} onChange={v => setProp(p.id, {
                  value: v.replace(/[^0-9]/g, '')
                })} data-id="element-194" data-mp="1i9zu0t6z3lsb" />
                      <TextField label="Monthly mortgage payment" prefix="$" inputMode="numeric" value={formatAmountInput(p.monthlyPayment)} onChange={v => setProp(p.id, {
                  monthlyPayment: v.replace(/[^0-9]/g, '')
                })} hint="Leave blank if paid off." data-id="element-195" data-mp="2qs3tmo6obi8" />
                    </FieldGrid>
                  </div>
                  <p className="mb-2 mt-5 text-sm font-medium text-ink-700" data-id="element-196" data-mp="3b5u17q63thpw">What are your plans for it?</p>
                  <div role="radiogroup" className="grid gap-2 sm:grid-cols-3" data-id="element-197" data-mp="3f8qdkz51whle">
                    {(['keep', 'sell', 'sold'] as const).map(v => <ChoiceCard key={v} title={v === 'keep' ? 'Keeping it' : v === 'sell' ? 'Selling before closing' : 'Already sold'} selected={p.plan === v} onSelect={() => setProp(p.id, {
                plan: v
              })} data-id="element-198" data-mp="31a8vpi1ttzbe" />)}
                  </div>
                </section>)}
              <Button variant="secondary" icon={<PlusIcon className="h-4 w-4" data-id="element-200" data-mp="26n5w0utvb6l1" />} onClick={() => update({
            properties: [...list, {
              id: uid('p'),
              address: '',
              value: '',
              plan: 'keep',
              hasMortgage: true,
              monthlyPayment: ''
            }]
          })} data-id="element-199" data-mp="14djewqqyajte">
                Add another property
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </StepFrame>;
}
const obligationTypes = ['Alimony', 'Child support', 'Separate maintenance', 'Job-related expenses'];
export function DebtsStep() {
  const {
    answers,
    update
  } = usePortal();
  const list = answers.obligations;
  const toggle = (t: string) => update({
    obligations: list.some(o => o.type === t) ? list.filter(o => o.type !== t) : [...list, {
      id: uid('o'),
      type: t,
      monthly: ''
    }]
  });
  return <StepFrame title="Your debts" description="Good news — you don’t need to type in your credit cards, car loans, or student loans." data-id="element-201" data-mp="2laqz4oqqt5sy">
      <div className="flex gap-4 rounded-2xl bg-white p-5 ring-1 ring-ink-200" data-id="element-202" data-mp="19x5ar5spj88h">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-pulse-50 text-pulse-600" data-id="element-203" data-mp="19v2bdv5o02r3">
          <CreditCardIcon className="h-5 w-5" aria-hidden data-id="element-204" data-mp="xet4ifedk9mj" />
        </span>
        <div data-id="element-205" data-mp="xgx78u0jf2l8">
          <p className="font-medium text-ink-900" data-id="element-206" data-mp="xh5ikhh0hzub">We’ll bring these in from your credit report</p>
          <p className="mt-1 text-sm leading-relaxed text-ink-500" data-id="element-207" data-mp="1bc7y7wbnv3kb">After you authorize credit, you’ll see your accounts here and can confirm or correct anything that looks off — like a card you’ve paid off.</p>
        </div>
      </div>

      <h2 className="mb-1 mt-10 font-medium text-ink-900" data-id="element-208" data-mp="ep4dgxdn9dcg">Do you pay any of these?</h2>
      <p className="mb-4 text-sm text-ink-500" data-id="element-209" data-mp="sfgb1bt004az">These don’t show up on credit reports, so we have to ask.</p>
      <div className="flex flex-wrap gap-2" data-id="element-210" data-mp="nohse5kjeu4k">
        {obligationTypes.map(t => {
        const on = list.some(o => o.type === t);
        return <button key={t} aria-pressed={on} onClick={() => toggle(t)} className={`h-10 rounded-full px-4 text-sm font-medium ring-1 ring-inset transition-[background-color,color,box-shadow] duration-150 ${on ? 'bg-pulse-600 text-white ring-pulse-600' : 'bg-white text-ink-700 ring-ink-200 hover:ring-ink-300'}`} data-id="element-211" data-mp="3w2qdyton6kn8">
              {t}
            </button>;
      })}
      </div>
      <div className="mt-4 space-y-3" data-id="element-212" data-mp="24t6k54z7qwbv">
        <AnimatePresence initial={false} data-id="element-213" data-mp="29r84dt8dl1ox">
          {list.map(o => <motion.div key={o.id} initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease
        }} className="overflow-hidden" data-id="element-214" data-mp="29tat89xskos2">
              <TextField label={o.type} prefix="$" suffix="/ month" inputMode="numeric" value={formatAmountInput(o.monthly)} onChange={v => update({
            obligations: list.map(x => x.id === o.id ? {
              ...x,
              monthly: v.replace(/[^0-9]/g, '')
            } : x)
          })} data-id="element-215" data-mp="1m1x1o67gsx85" />
            </motion.div>)}
        </AnimatePresence>
      </div>
      {list.length === 0 && <div className="mt-6" data-id="element-216" data-mp="31h2sjmkhylc0">
          <Callout data-id="element-217" data-mp="31kibx1l7egj8">
            <span className="flex items-center gap-2" data-id="element-218" data-mp="pv2a59b8z5cs">
              <ShieldCheckIcon className="h-4 w-4" aria-hidden data-id="element-219" data-mp="puiszutf8kzt" /> None of these apply? Just continue.
            </span>
          </Callout>
        </div>}
    </StepFrame>;
}